import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, MessageSquareText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  mapReviewError,
  useAdminReviewedProductOptions,
  useAdminReviews,
  useAuthSession,
  useTranslation,
} from '@dukanti/shared';
import type { AdminProductReviewRow, AdminReviewVisibilityFilter } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useCourierPermissions } from '@/hooks/use-courier-permissions';
import {
  ReviewResponseDialog,
  ReviewVisibilityDialog,
  ReviewsFilterBar,
  ReviewsMobileCards,
  ReviewsTable,
} from '@/features/reviews';

const PAGE_SIZE = 20;
const REVIEWED_PRODUCT_OPTION_PAGE_SIZE = 50;

function parseVisibility(value: string | null): AdminReviewVisibilityFilter {
  return value === 'visible' || value === 'hidden' ? value : 'all';
}

export function ReviewsRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const permissionsQuery = useCourierPermissions(storeId);
  const [productOptionSearch, setProductOptionSearch] = useState('');
  const [productOptionOffset, setProductOptionOffset] = useState(0);
  const reviewedProductsQuery = useAdminReviewedProductOptions({
    client: supabase,
    storeId,
    search: productOptionSearch,
    limit: REVIEWED_PRODUCT_OPTION_PAGE_SIZE,
    offset: productOptionOffset,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [respondingReview, setRespondingReview] = useState<AdminProductReviewRow | null>(null);
  const [visibilityReview, setVisibilityReview] = useState<AdminProductReviewRow | null>(null);

  useEffect(() => {
    setProductOptionOffset(0);
    setProductOptionSearch('');
  }, [storeId]);

  const productId = searchParams.get('product') || null;
  const products = useMemo(() => {
    const options = (reviewedProductsQuery.data ?? []).map((option) => ({
      id: option.product_id,
      name: option.product_name,
    }));
    if (productId && !options.some((option) => option.id === productId)) {
      options.unshift({ id: productId, name: productId });
    }
    return options;
  }, [productId, reviewedProductsQuery.data]);
  const visibility = parseVisibility(searchParams.get('visibility'));
  const canModerate = permissionsQuery.data?.canManageCouriers === true;
  const hasMoreReviewedProductOptions =
    (reviewedProductsQuery.data?.length ?? 0) === REVIEWED_PRODUCT_OPTION_PAGE_SIZE;

  const filters = useMemo(
    () =>
      storeId
        ? {
            storeId,
            productId,
            visibility,
            limit: PAGE_SIZE,
            offset,
          }
        : null,
    [offset, productId, storeId, visibility],
  );
  const reviewsQuery = useAdminReviews({ client: supabase, filters });
  const rows = reviewsQuery.data?.rows ?? [];

  function updateParams(next: {
    readonly productId?: string | null;
    readonly visibility?: AdminReviewVisibilityFilter;
  }): void {
    const params = new URLSearchParams(searchParams);
    if ('productId' in next) {
      if (next.productId) params.set('product', next.productId);
      else params.delete('product');
    }
    if ('visibility' in next) {
      if (next.visibility && next.visibility !== 'all') params.set('visibility', next.visibility);
      else params.delete('visibility');
    }
    setSearchParams(params, { replace: true });
    setOffset(0);
  }

  return (
    <main
      dir={dir}
      data-testid="reviews-route"
      className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div className="text-start">
          <h1 className="font-display text-foreground text-[28px] font-bold leading-tight">
            {t('admin.reviews.title')}
          </h1>
        </div>
      </div>

      <ReviewsFilterBar
        products={products}
        productId={productId}
        productSearch={productOptionSearch}
        productOptionsLoading={reviewedProductsQuery.isFetching}
        productOptionsCanPreviousPage={productOptionOffset > 0}
        productOptionsHasNextPage={hasMoreReviewedProductOptions}
        visibility={visibility}
        onProductChange={(nextProductId) => updateParams({ productId: nextProductId })}
        onProductSearchChange={(nextSearch) => {
          setProductOptionSearch(nextSearch);
          setProductOptionOffset(0);
        }}
        onProductOptionsPreviousPage={() =>
          setProductOptionOffset((current) =>
            Math.max(0, current - REVIEWED_PRODUCT_OPTION_PAGE_SIZE),
          )
        }
        onProductOptionsNextPage={() =>
          setProductOptionOffset((current) => current + REVIEWED_PRODUCT_OPTION_PAGE_SIZE)
        }
        onVisibilityChange={(nextVisibility) => updateParams({ visibility: nextVisibility })}
      />

      {reviewsQuery.isError ? (
        <div
          role="alert"
          data-testid="reviews-route-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" aria-hidden />
          <h2 className="text-foreground mb-2 text-xl font-semibold">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t(mapReviewError(reviewsQuery.error))}
          </p>
          <Button type="button" variant="outline" onClick={() => void reviewsQuery.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {!reviewsQuery.isError && reviewsQuery.isLoading ? (
        <div
          className="border-border bg-card shadow-admin-card rounded-lg border p-8"
          aria-busy="true"
          data-testid="reviews-route-loading"
        >
          <div className="bg-muted h-5 w-48 animate-pulse rounded" />
          <div className="bg-muted mt-4 h-32 animate-pulse rounded" />
        </div>
      ) : null}

      {!reviewsQuery.isError && !reviewsQuery.isLoading && rows.length === 0 ? (
        <div
          className="border-border bg-card shadow-admin-card rounded-lg border p-8 text-center"
          data-testid="reviews-empty-state"
        >
          <MessageSquareText className="text-muted-foreground mx-auto mb-4 size-10" aria-hidden />
          <h2 className="text-foreground mb-2 text-xl font-semibold">
            {t('admin.reviews.emptyTitle')}
          </h2>
          <p className="text-muted-foreground mx-auto max-w-md text-sm">
            {t('admin.reviews.emptyDescription')}
          </p>
        </div>
      ) : null}

      {!reviewsQuery.isError && rows.length > 0 ? (
        <div className="space-y-4">
          <ReviewsTable
            rows={rows}
            canModerate={canModerate}
            onRespond={setRespondingReview}
            onVisibility={setVisibilityReview}
          />
          <ReviewsMobileCards
            rows={rows}
            canModerate={canModerate}
            onRespond={setRespondingReview}
            onVisibility={setVisibilityReview}
          />
          {reviewsQuery.data?.hasMore ? (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOffset((current) => current + PAGE_SIZE)}
              >
                {t('admin.reviews.loadMore')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <ReviewResponseDialog
        review={respondingReview}
        open={respondingReview !== null}
        onOpenChange={(open) => {
          if (!open) setRespondingReview(null);
        }}
      />
      <ReviewVisibilityDialog
        review={visibilityReview}
        open={visibilityReview !== null}
        onOpenChange={(open) => {
          if (!open) setVisibilityReview(null);
        }}
      />
    </main>
  );
}
